import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { useCachedSearchTopOffers } from 'components/Accommodation/Offers/useCachedSearchTopOffers';
import { getAnchorPriceNightlyPerRoom, getShowTotalPrices } from 'modules/sapiSearch/selectors';
import { getAnchorPriceTotal } from 'modules/sapiSearch/utils';
import { getIsMultiRoomBundlesSearch } from 'modules/search/selectors';
import { RootState } from 'store';
import { ButtonLink } from '@daedalus/atlas/ButtonLink';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Price } from '@daedalus/atlas/Price';
import { Text } from '@daedalus/atlas/Text';
import { defaultCurrencyCode } from '@daedalus/core/src/localization/business/currency';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { hasAnchorPrice as getHasAnchorPrice } from '@daedalus/core/src/offer/business/price';
import { OfferPrice } from '@daedalus/core/src/offer/types/offer';
import { getDisplayPrice, getNightlyPriceForAllRooms, getTotalPrice } from '@daedalus/core/src/price/business/price';
import { RoomWithoutOffers, SplitBookingDates } from '@daedalus/core/src/room/types/room';
import { getRoundingStrategy } from '@daedalus/core/src/utils/number';
import { PriceBreakdownModal } from './PriceBreakdownModal';
type Prop = {
  hotelId: string;
  prices: OfferPrice[];
  splitBookingRooms: RoomWithoutOffers[];
  splitBookingDates: SplitBookingDates;
  propertyCurrencyCode: string;
};
const BaselineRow = /*#__PURE__*/styled('div')({
  name: "BaselineRow",
  class: "b98phlv",
  propsAsIs: false
});
const _exp = /*#__PURE__*/() => Text;
const PostFix = /*#__PURE__*/styled(_exp())({
  name: "PostFix",
  class: "p17850vm",
  propsAsIs: true
});
const SecondaryPriceWrapper = /*#__PURE__*/styled('div')({
  name: "SecondaryPriceWrapper",
  class: "s1e30vtc",
  propsAsIs: false
});
const _exp3 = /*#__PURE__*/() => Flex.Row;
const WhiteSpaceNowrapRow = /*#__PURE__*/styled(_exp3())({
  name: "WhiteSpaceNowrapRow",
  class: "wwj78xs",
  propsAsIs: true
});
const _exp4 = /*#__PURE__*/() => WhiteSpaceNowrapRow;
const BasePriceRow = /*#__PURE__*/styled(_exp4())({
  name: "BasePriceRow",
  class: "b1ew5nsv",
  propsAsIs: true
});
const _exp5 = /*#__PURE__*/() => ButtonLink;
const PriceDetailsButton = /*#__PURE__*/styled(_exp5())({
  name: "PriceDetailsButton",
  class: "p1keibx1",
  propsAsIs: true
});
const IncludingTaxesMessage = /*#__PURE__*/styled('span')({
  name: "IncludingTaxesMessage",
  class: "iriarut",
  propsAsIs: false
});
const roundingStrategy = getRoundingStrategy();
const makeGetPriceValues = ({
  anchorPrice,
  anchorPriceTotal,
  numberOfRooms,
  showTotalPrices
}: {
  anchorPrice: number;
  anchorPriceTotal: number;
  numberOfRooms: number;
  showTotalPrices: boolean;
}) => (prices: OfferPrice[]) => {
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch);
  const displayPrice = getDisplayPrice(prices);
  const totalPrice = getTotalPrice(displayPrice);
  const calculatedNightlyPrice = getNightlyPriceForAllRooms(displayPrice.chargeable.nightlyDisplayTotal, isMultiRoomBundlesSearch, numberOfRooms);
  const calculatedAnchorPrice = getNightlyPriceForAllRooms(anchorPrice, isMultiRoomBundlesSearch, numberOfRooms);
  return showTotalPrices ? {
    anchorPriceValue: anchorPriceTotal * numberOfRooms,
    mainPriceValue: totalPrice,
    detailsPriceValue: calculatedNightlyPrice
  } : {
    anchorPriceValue: calculatedAnchorPrice,
    mainPriceValue: calculatedNightlyPrice,
    detailsPriceValue: totalPrice
  };
};
const postFixMessages = defineMessages({
  total: {
    id: 'total',
    defaultMessage: 'total'
  },
  perNight: {
    id: 'perNight',
    defaultMessage: '/ night'
  }
});
export const RoomsOfferPrice = ({
  hotelId,
  prices,
  splitBookingRooms,
  splitBookingDates,
  propertyCurrencyCode
}: Prop) => {
  const [openPriceBreakdownOverlay, setOpenPriceBreakdownOverlay] = useState(false);
  const {
    formatMessage
  } = useIntl();
  const {
    offerEntity
  } = useCachedSearchTopOffers({
    hotelId
  });
  const anchorPrice = useSelector((state: RootState) => getAnchorPriceNightlyPerRoom(state, offerEntity));
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity);
  const displayPrice = getDisplayPrice(prices);
  const {
    currencyCode: displayCurrencyCode
  } = displayPrice.chargeable;
  const currencyCode = displayCurrencyCode || defaultCurrencyCode;
  const showTotalPrices = useSelector(getShowTotalPrices);
  const getPriceValues = makeGetPriceValues({
    anchorPrice,
    anchorPriceTotal,
    numberOfRooms: 1,
    showTotalPrices
  });
  const onClickPriceDetails = () => {
    setOpenPriceBreakdownOverlay(true);
  };
  const {
    anchorPriceValue,
    mainPriceValue,
    detailsPriceValue
  } = getPriceValues(prices);
  const showAnchorPrice = !isNaN(anchorPrice) && anchorPrice && getHasAnchorPrice(mainPriceValue, anchorPriceValue);
  const isMainPriceDifferentFromDetailsPrice = mainPriceValue !== detailsPriceValue;
  const pricePostFix = showTotalPrices ? postFixMessages.total : postFixMessages.perNight;
  const priceDetailPostFix = showTotalPrices ? postFixMessages.perNight : postFixMessages.total;
  return <>
      <Flex.Column wrap="wrap" alignItems="flex-end">
        <BasePriceRow alignItems="baseline" justifyContent="flex-end" gap="s200">
          {showAnchorPrice ? <Price isAnchor currency={currencyCode} value={anchorPriceValue} roundingStrategy={roundingStrategy} dataId="splitBooking.anchorPrice" /> : null}

          <Price showLockIcon={false} currency={currencyCode} value={mainPriceValue} roundingStrategy={roundingStrategy} size="lg" dataId="splitBooking.nightlyPrice" />
          <PostFix variant="bodyS">{formatMessage(pricePostFix)}</PostFix>
        </BasePriceRow>

        {isMainPriceDifferentFromDetailsPrice && <PriceDetailsButton variant="info" isCompact onClick={onClickPriceDetails} size="sm">
            <Text as="div" variant="labelS">
              <SecondaryPriceWrapper data-id="splitBooking.totalPrice">
                <Price currency={currencyCode} value={detailsPriceValue} roundingStrategy={roundingStrategy} noStyle />
                <BaselineRow>
                  &nbsp;
                  {formatMessage(priceDetailPostFix)}
                </BaselineRow>
              </SecondaryPriceWrapper>
              <Flex.Row justifyContent="flex-end">
                <IncludingTaxesMessage>
                  <FormattedMessageWrapper id="includingTaxes" defaultMessage="incl tax and fees" />
                </IncludingTaxesMessage>
              </Flex.Row>
            </Text>
          </PriceDetailsButton>}
      </Flex.Column>
      <PriceBreakdownModal isOpen={openPriceBreakdownOverlay} onClose={() => setOpenPriceBreakdownOverlay(false)} prices={prices} splitBookingRooms={splitBookingRooms} splitBookingDates={splitBookingDates} propertyCurrencyCode={propertyCurrencyCode} />
    </>;
};

require("./RoomsOfferPrice.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./RoomsOfferPrice.tsx");